// @angular
import {
  Component,
  OnDestroy,
  OnInit,
  HostListener,
  ViewChild,
  NgZone,
  effect,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Router, NavigationEnd, NavigationStart } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
// Own services
import { SessionDataService } from "./services/shared/SessionDataService.service";
import { TokenCheckService } from "./services/shared/TokenCheckService.service";
import { BrowserStorageLocalService } from "./services/shared/BrowserStorageServices/BrowserStorageLocalService.service";
import { ToastService } from "./services/shared/ToastService.service";
import { ThemeService } from "./themes/theme.service";
import { LoginControllerService } from "./services/server/LoginController.service";
import { UserControllerService } from "./services/server/UserController.service";
import { RedirectToService } from "./services/shared/RedirectToService.service";
import { CheckForUpdateService } from "./services/shared/CheckForUpdateService.service";
import { MainPanelComponent } from "./modules/panel-module/main-panel/main-panel.component";
import { CustomUrlService } from "./services/shared/CustomUrl.service";
import { DateParserService } from "./services/shared/DateParserService.service";
import { LanguageControllerService } from "./services/language/LanguageController.service";
// Interfaces
import { SessionData } from "./interfaces/SessionGlobalInterface.type";
import { Theme } from "./themes/symbols";
// Version
import packageJson from "../../package.json";
// Variables
import { PROFILES } from "../assets/profiles/profiles";
import { LoginControllerComponent } from "./screens/login/login-controller/login-controller.component";
import { MaintenanceComponent } from "./screens/errors/maintenance/maintenance.component";

import { ThemeDirective } from "./themes/theme.directive";
import { ChangePasswordController } from "./screens/login/change-password-controller/change-password-controller";
import { UserConfig } from "./interfaces/UserGlobalInterface.type";

/**
 * Root shell for session, authentication, maintenance, language, and theme state.
 * Persists session context on route changes and records interrupted sessions on window unload.
 */
@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ThemeDirective,
    MaintenanceComponent,
    LoginControllerComponent,
    ChangePasswordController,
    MainPanelComponent
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  userLogged: boolean;
  startUrl: string;
  profileStartUrl: string;
  maintenance: boolean = false;
  themeUpdatedSub: Subscription;

  // Router event subscriptions
  routerEventsStart: Subscription;
  routerEventsNewTab: Subscription;
  routerEventsEnd: Subscription;

  // Session data
  sessionProfile: string;
  sessionData: SessionData = this.BrowserStorageLocalService.getJsonValue("session");
  loadSession: boolean;
  appVersion: { module: string; version: string }[];
  frontVersion = {
    module: "Web Front",
    version: packageJson.version
  };
  // Body
  body = document.body;
  changePasswordRequired: boolean = false;

  // Marks the stored session as interrupted and saves its current context before the tab or browser closes
  @HostListener("window:beforeunload")
  beforeUnloadHandler() {
    if (!this.router.url.includes("/acceso-denegado")) {
      let updatedSessionData = this.BrowserStorageLocalService.getJsonValue("session");
      updatedSessionData["sessionNotFinished"] = true;
      this.updateSessionVariable(updatedSessionData);
      this.BrowserStorageLocalService.setJsonValue("session", updatedSessionData);
    }
  }

  // BroadcastChannel reserved for Service Worker messages
  channel4Broadcast = new BroadcastChannel("arsonChannel");
  @ViewChild(MainPanelComponent) mainPanel: MainPanelComponent;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly BrowserStorageLocalService: BrowserStorageLocalService,
    private readonly CheckForUpdateService: CheckForUpdateService,
    private readonly CustomUrlService: CustomUrlService,
    private readonly DateParserService: DateParserService,
    private readonly LanguageController: LanguageControllerService,
    private readonly LoginController: LoginControllerService,
    private readonly RedirectToService: RedirectToService,
    private readonly router: Router,
    private readonly SessionDataService: SessionDataService,
    private readonly theme: ThemeService,
    private readonly ToastService: ToastService,
    private readonly TokenCheckService: TokenCheckService,
    private readonly translate: TranslateService,
    private readonly UserController: UserControllerService,
    private readonly zone: NgZone
  ) {
    // Maintenance
    effect(() => {
      const maintenance = this.SessionDataService.maintenance();
      if (maintenance !== null) {
        this.logout();
        this.maintenance = maintenance;
      }
    });
    // User changes
    effect(() => {
      const user = this.SessionDataService.user();
      if (user) {
        this.theme.loadThemeConfig(user);
      }
    });
    // Profile changes
    effect(() => {
      const profile = this.SessionDataService.profile();
      if (profile) {
        this.sessionProfile = profile;
        if (this.sessionProfile === PROFILES.ARSON) {
          this.getBackVersion();
        } else {
          this.appVersion = [this.frontVersion];
        }
      }
    });
    // Session expired
    effect(() => {
      if (this.SessionDataService.sessionExpiredTick() > 0 && this.userLogged) {
        this.sessionExpired();
      }
    });
    // HTTP 401 error
    effect(() => {
      if (this.SessionDataService.httpError401Tick() > 0) {
        this.checkTokenForHttpError401();
      }
    });
    // Logout
    effect(() => {
      if (this.SessionDataService.logoutTick() > 0) {
        this.logout();
      }
    });
    // Language changes
    effect(() => {
      const language = this.SessionDataService.language();
      if (language) {
        let updatedSessionData = this.BrowserStorageLocalService.getJsonValue("session");
        if (updatedSessionData) {
          updatedSessionData["language"] = language;
          this.BrowserStorageLocalService.setJsonValue("session", updatedSessionData);
        }
        this.getLanguage(language);
        this.translate.use(language);
        this.DateParserService.locale(language);
        if (this.userLogged && !untracked(() => this.SessionDataService.sessionLoadingFlag())) {
          setTimeout(() => location.reload(), 0);
        }
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit() {
    // Current theme colors
    this.getCurrentColors(this.theme.getActiveTheme());

    // Style updates
    this.themeUpdatedSub = this.theme.themeChange.subscribe((activeTheme: Theme) =>
      this.getCurrentColors(activeTheme)
    );

    // Applies client-specific title, favicon and theme from the URL
    this.CustomUrlService.checkCustomUrl();

    // Captures the initial URL in case of a reload or new tab
    this.startUrl = window.location.href.includes("#")
      ? window.location.href.substring(window.location.href.indexOf("#") + 1)
      : "/principal";

    // Detects a session started from a new tab
    if (this.sessionData?.avoidNavigationReload) {
      this.SessionDataService.sendAvoidNavigationReload(true);
      // Clear the flag so it does not persist across later reloads
      const cleanedSession = { ...this.sessionData };
      delete cleanedSession["avoidNavigationReload"];
      this.BrowserStorageLocalService.setJsonValue("session", cleanedSession);
    }

    // Checks the stored token
    if (this.sessionData) {
      this.checkToken();
      if (this.sessionData.mustChangePassword) {
        this.changePasswordRequired = true;
      }
    } else {
      this.userLogged = false;
      this.loadSession = false;
    }

    // Keeps SweetAlert2 popups above the CDK overlay
    const swalObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach((node: any) => {
          if (node.classList?.contains("swal2-container")) {
            (node as HTMLElement).style.zIndex = "99999";
          }
        });
      });
    });
    swalObserver.observe(document.body, { childList: true });

    // New-tab check
    this.routerEventsNewTab = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        const newTab = untracked(() => this.SessionDataService.newTab());
        const stateData = untracked(() => this.router.currentNavigation()?.extras?.state?.data);
        if (newTab) {
          this.RedirectToService.openNewTab(event.url, stateData);
        }
      });

    // Listens for URL changes when navigation ends
    this.routerEventsEnd = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Updates session data on URL change
        this.updateSessionData(event);
      });

    // Disabled Service Worker message handling
    // this.SessionDataService.sendChannel4Broadcast(this.channel4Broadcast);
    // this.channel4Broadcast.onmessage = event => {
    //   this.processMessage(event.data);
    // };

    // Session timeout
    this.TokenCheckService.tokenTimeout();
    this.changePasswordRequired = untracked(() => this.SessionDataService.mustChangePassword());

    // Version check
    setTimeout(() => this.CheckForUpdateService.startChecking(), 0);
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy() {
    this.channel4Broadcast.close();
    this.routerEventsStart.unsubscribe();
    this.routerEventsNewTab.unsubscribe();
    this.routerEventsEnd.unsubscribe();
    this.themeUpdatedSub.unsubscribe();
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Gets the current theme colors
  getCurrentColors(theme: Theme): void {
    if (theme) {
      let currentColors = {};
      for (let color in theme.properties) {
        currentColors[color.replace("--color-", "").replace("-", "_").toUpperCase()] =
          theme.properties[color];
      }
      this.SessionDataService.sendColors(currentColors);
    }
  }

  // Login succeeded
  loginSucceed(startUrl: string): void {
    if (startUrl === "change-password") {
      this.userLogged = false;
      this.changePasswordRequired = true;
      return;
    }
    this.userLogged = true;
    this.SessionDataService.sendUserLogged(this.userLogged);
    this.changePasswordRequired = untracked(() => this.SessionDataService.mustChangePassword());
    this.startUrl = startUrl;
    this.profileStartUrl = startUrl;
    if (this.sessionProfile === PROFILES.ARSON) {
      this.UserController.updateRestrictions(true).subscribe();
    }
  }

  getLanguage(language?: string): void {
    language = language || this.translate.currentLang || this.translate.getDefaultLang();
    this.LanguageController.getLang(language).subscribe(response => {
      const arTranslations = response["body"]?.arTranslations;
      const sharedTranslations = response["body"]?.translations;
      if (sharedTranslations) {
        const translations = arTranslations
          ? this.LanguageController.mergeI18nSections(arTranslations, sharedTranslations)
          : this.LanguageController.mergeI18nSections(sharedTranslations);
        this.translate.setTranslation(language, translations, true);
        this.SessionDataService.sendChatbotEnabled(this.sessionProfile === PROFILES.ARSON);
      } else if (this.sessionProfile === PROFILES.ARSON) {
        this.ToastService.fireToast("error", this.translate.instant("translation-load-error"));
      }
    });
  }

  // Logout
  logout(): void {
    let userConfig = this.BrowserStorageLocalService.getJsonValue("config");
    this.SessionDataService.clearDataProtection();
    localStorage.clear();
    sessionStorage.clear();
    this.BrowserStorageLocalService.setJsonValue("config", userConfig);
    this.userLogged = false;
    this.loadSession = false;
    this.SessionDataService.sendUserLogged(this.userLogged);
    this.router.navigate(["/"]);
  }

  // Checks the active user token
  checkToken(): void {
    this.TokenCheckService.checkAlive().subscribe(response => {
      let alive = response["body"]?.tokenValid;
      this.SessionDataService.sendOriginalProfile(
        response["body"]?.isArson ? PROFILES.ARSON : null
      );
      if (alive) {
        if (this.sessionData.mustChangePassword) {
          this.changePasswordRequired = true;
          this.userLogged = false;
          this.loadSession = false;
        } else {
          this.loadSession = true;
          this.SessionDataService.sendSessionLoadingFlag(true);
          this.userLogged = true;
          if (this.sessionData.language) {
            this.SessionDataService.sendLanguage(this.sessionData.language);
          }
          this.SessionDataService.sendUserLogged(this.userLogged);
          this.SessionDataService.sendProfile(this.sessionData.profile);
          this.SessionDataService.sendTelemetryActive(this.sessionData.isTlm);
          this.SessionDataService.sendUser(this.sessionData.user);
          this.SessionDataService.sendUserId(this.sessionData.userId);
          let configData = this.getUserLocalConfig();
          if (configData?.mainPage) {
            this.SessionDataService.sendMainPageUpdatedFlag(configData.mainPage);
          }
          if (this.sessionData.profile === PROFILES.ARSON) {
            this.UserController.updateRestrictions(true).subscribe();
          }
        }
      } else {
        this.userLogged = false;
        this.loadSession = false;
      }
    });
  }

  // Gets the local user config
  getUserLocalConfig(): UserConfig {
    let configData: UserConfig[] = this.BrowserStorageLocalService.getJsonValue("config");
    return configData?.find((config: UserConfig) => config.user === this.sessionData.user);
  }

  // Checks the token after an HTTP 401 error (unauthorized)
  checkTokenForHttpError401(): void {
    this.TokenCheckService.checkAlive().subscribe(response => {
      if (!response["body"]?.tokenValid) {
        this.sessionExpired();
      }
    });
  }

  // Updates the localStorage session variable on route change
  updateSessionData(event: NavigationEnd): void {
    // A new tab was just opened: do not overwrite the context
    // openNewTab left in localStorage (the new tab has not read it yet)
    if (this.RedirectToService.skipNextSessionUpdate) {
      this.RedirectToService.skipNextSessionUpdate = false;
      return;
    }
    if (
      this.userLogged && // Logged-in user check
      untracked(() => this.SessionDataService.user()) && // Current user check
      (event
        ? !event.url.includes("/acceso-denegado")
        : !this.router.url.includes("/acceso-denegado")) && // Correct route check
      !this.loadSession // Session load check
    ) {
      let updatedSessionData = this.BrowserStorageLocalService.getJsonValue("session");
      if (updatedSessionData) {
        this.updateSessionVariable(updatedSessionData, event);
        this.BrowserStorageLocalService.setJsonValue("session", updatedSessionData);
      }
    }

    // Re-enables scrollbars in the subscriber UI
    if (this.sessionProfile === PROFILES.ABONADO) {
      setTimeout(() => (this.body.style.overflow = "visible"), 500);
    }
  }

  // Session expired
  sessionExpired(): void {
    this.SessionDataService.sendNotificationLoop(false);
    this.ToastService.fireToastWithConfirmation(
      "warning",
      this.translate.instant("session-expired"),
      this.translate.instant("renew-session"),
      this.translate.instant("cancel")
    ).then(renewSession => {
      if (renewSession) {
        this.ToastService.firePasswordInput().then((password: string) => {
          if (password) {
            this.TokenCheckService.renewSession(password);
            this.SessionDataService.sendNotificationLoop(true);
          } else {
            this.logout();
          }
        });
      } else {
        this.logout();
      }
    });
  }

  // Updates the session variable data
  updateSessionVariable(sessionData: any, event?: NavigationEnd): void {
    sessionData["user"] = untracked(() => this.SessionDataService.user());
    sessionData["profile"] = untracked(() => this.SessionDataService.profile());
    sessionData["isTlm"] = untracked(() => this.SessionDataService.telemetryActive());
    sessionData["language"] = untracked(() => this.SessionDataService.language());
    sessionData["numberFormat"] = untracked(() => this.SessionDataService.numberFormat());
    sessionData["dateFormat"] = untracked(() => this.SessionDataService.dateFormat());
    sessionData["current"] = {};
    sessionData["current"]["client"] = untracked(() => this.SessionDataService.client());
    sessionData["current"]["agrupation"] = untracked(() => this.SessionDataService.agrupation());
    sessionData["current"]["entity"] = untracked(() => this.SessionDataService.entity());
    sessionData["urlData"] = history.state.data;
    sessionData["url"] = event ? event.url : this.router.url;
    sessionData["profileStartUrl"] = this.profileStartUrl;
  }

  // Gets the back-end module versions
  getBackVersion(): void {
    this.LoginController.getVersions().subscribe(response => {
      let appVersion = [
        this.frontVersion,
        {
          module: "Web",
          version: response["body"]?.web ? response["body"]?.web : this.translate.instant("unknown")
        },
        {
          module: "Data Processor Web",
          version: response["body"]?.dataProcessorWeb
            ? response["body"]?.dataProcessorWeb
            : this.translate.instant("unknown")
        },
        {
          module: "Communication Web",
          version: response["body"]?.communicationWeb
            ? response["body"]?.communicationWeb
            : this.translate.instant("unknown")
        },
        {
          module: "Data Processor 1",
          version: response["body"]?.dataProcessor1
            ? response["body"]?.dataProcessor1
            : this.translate.instant("unknown")
        },
        {
          module: "Data Processor 2",
          version: response["body"]?.dataProcessor2
            ? response["body"]?.dataProcessor2
            : this.translate.instant("unknown")
        },
        {
          module: "File Manager",
          version: response["body"]?.gestorFicheros
            ? response["body"]?.gestorFicheros
            : this.translate.instant("unknown")
        },
        {
          module: "Communication",
          version: response["body"]?.communicationGestiorFicheros
            ? response["body"]?.communicationGestiorFicheros
            : this.translate.instant("unknown")
        }
      ];
      this.appVersion = appVersion;
    });
  }

  // Processes a message from the broadcast channel
  processMessage(message: { action: string; data?: any }): void {
    // Example of sending a message
    // this.SessionDataService
    // .getCurrentChannel4Broadcast()
    // ?.postMessage({ action: "goTo", data: "/principal" });

    switch (message.action) {
      case "reloadPanel":
        this.zone.run(() => this.mainPanel.reloadPanel());
        break;
      case "reloadTab":
        window.location.reload();
        break;
      case "goTo":
        this.router.navigate([message.data]);
        break;
      default:
        break;
    }
  }
}
